import {
  Cog6ToothIcon,
  HeartIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { Trans } from "@lingui/react/macro";
import { ReactNode } from "react";
import { useRouter } from "router2";

import { AppLink } from "~/ui/base/AppLink.tsx";
import { WebPokeSheet } from "~/ui/overlay/WebPokeSheet.tsx";
import { WebShowDrawingSheet } from "~/ui/overlay/WebShowDrawingSheet.tsx";
import { WebShowGeolocationSheet } from "~/ui/overlay/WebShowGeolocationSheet.tsx";
import { WebShowPictureSheet } from "~/ui/overlay/WebShowPictureSheet.tsx";
import { useUser } from "~/ui/provider/Auth.tsx";
import { releaseToken } from "~/ui/provider/PwaProvider.tsx";
import {
  type OverlaySheets,
  SheetsContext,
} from "~/ui/provider/StackedLayerProvider.tsx";

interface WebLayoutProps {
  children: ReactNode;
}

const webSheets: OverlaySheets = {
  poke: WebPokeSheet,
  showDrawing: WebShowDrawingSheet,
  showGeolocation: WebShowGeolocationSheet,
  showPicture: WebShowPictureSheet,
};

export const WebLayout = ({ children }: WebLayoutProps) => {
  const { pathname, push } = useRouter();
  const { isLoggedIn, myInfo } = useUser();

  const navItems = [
    {
      href: "/web/search",
      icon: MagnifyingGlassIcon,
      label: <Trans>콕 찌르기</Trans>,
    },
    { href: "/web/like", icon: HeartIcon, label: <Trans>팔로잉</Trans> },
    { href: "/web/my-page", icon: UserIcon, label: <Trans>마이페이지</Trans> },
    { href: "/web/setting", icon: Cog6ToothIcon, label: <Trans>설정</Trans> },
  ];

  return (
    <SheetsContext.Provider value={webSheets}>
      <div className="flex min-h-screen flex-col bg-white text-zinc-900 md:flex-row dark:bg-zinc-950 dark:text-zinc-100">
        {/* Sidebar & Responsive Top Header */}
        <aside className="sticky top-0 z-30 flex w-full flex-col justify-between border-b border-zinc-100 bg-white/90 p-4 backdrop-blur-md md:h-screen md:w-60 md:shrink-0 md:border-r md:border-b-0 md:border-zinc-200/50 md:p-5 dark:border-zinc-800/50 dark:bg-zinc-950/90">
          <div>
            <div className="flex items-center justify-between md:block">
              <button
                className="flex items-center text-left"
                onClick={() => {
                  push({ pathname: "/web" });
                }}
                type="button"
              >
                <h1 className="text-xl font-bold italic">
                  <Trans>요기콕콕!</Trans> 👉
                </h1>
              </button>

              {/* Mobile Top User / Auth Info */}
              <div className="flex items-center gap-2 md:hidden">
                {isLoggedIn && myInfo ? (
                  <div className="flex items-center gap-2">
                    <button
                      className="flex items-center gap-2"
                      onClick={() => {
                        push({ pathname: "/web/my-page" });
                      }}
                      type="button"
                    >
                      <img
                        alt={myInfo.name}
                        className="size-7 rounded-full object-cover"
                        src={
                          myInfo.profileImageUrl ??
                          "/asset/default_user_profile.png"
                        }
                      />
                    </button>
                    <button
                      className="text-xs text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                      onClick={() => {
                        releaseToken();
                        location.pathname = "/web";
                      }}
                      type="button"
                    >
                      <Trans>로그아웃</Trans>
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-1.5">
                    <button
                      className="rounded-lg bg-black px-3 py-1.5 text-xs font-medium text-white dark:bg-zinc-800"
                      onClick={() => {
                        push({ pathname: "/web/sign-in" });
                      }}
                      type="button"
                    >
                      <Trans>로그인</Trans>
                    </button>
                    <button
                      className="rounded-lg bg-zinc-100 px-3 py-1.5 text-xs font-medium text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200"
                      onClick={() => {
                        push({ pathname: "/web/register" });
                      }}
                      type="button"
                    >
                      <Trans>회원가입</Trans>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation for Desktop */}
            <nav className="mt-8 hidden flex-col gap-2 md:flex">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = pathname.startsWith(item.href);

                return (
                  <AppLink
                    className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium duration-150 ${
                      isActive
                        ? "bg-zinc-100 text-black dark:bg-zinc-800 dark:text-white"
                        : "text-zinc-600 hover:bg-zinc-50 dark:text-zinc-400 dark:hover:bg-zinc-900"
                    }`}
                    key={item.href}
                    pathname={item.href}
                  >
                    <Icon className="size-5" />
                    <span>{item.label}</span>
                  </AppLink>
                );
              })}
            </nav>
          </div>

          {/* Desktop Bottom User Profile / Auth */}
          <div className="hidden pt-4 md:block">
            {isLoggedIn && myInfo ? (
              <div className="flex items-center justify-between gap-2">
                <button
                  className="flex min-w-0 items-center gap-2 text-left"
                  onClick={() => {
                    push({ pathname: "/web/my-page" });
                  }}
                  type="button"
                >
                  <img
                    alt={myInfo.name}
                    className="size-8 rounded-full object-cover"
                    src={
                      myInfo.profileImageUrl ??
                      "/asset/default_user_profile.png"
                    }
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">
                      {myInfo.name}
                    </p>
                    <p className="truncate text-xs text-zinc-400">
                      @{myInfo.email}
                    </p>
                  </div>
                </button>
                <button
                  className="shrink-0 text-xs whitespace-nowrap text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                  onClick={() => {
                    releaseToken();
                    location.pathname = "/web";
                  }}
                  type="button"
                >
                  <Trans>로그아웃</Trans>
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  className="flex-1 rounded-xl bg-black py-2 text-center text-sm font-medium text-white dark:bg-zinc-800"
                  onClick={() => {
                    push({ pathname: "/web/sign-in" });
                  }}
                  type="button"
                >
                  <Trans>로그인</Trans>
                </button>
                <button
                  className="flex-1 rounded-xl bg-zinc-100 py-2 text-center text-sm font-medium text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200"
                  onClick={() => {
                    push({ pathname: "/web/register" });
                  }}
                  type="button"
                >
                  <Trans>회원가입</Trans>
                </button>
              </div>
            )}
          </div>

          {/* Navigation for Mobile Viewports */}
          <nav className="mt-3 flex justify-around border-t border-zinc-100 pt-2 md:hidden dark:border-zinc-900">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = pathname.startsWith(item.href);

              return (
                <AppLink
                  className={`flex flex-col items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium duration-150 ${
                    isActive
                      ? "text-black dark:text-white"
                      : "text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                  }`}
                  key={item.href}
                  pathname={item.href}
                >
                  <Icon className="size-5" />
                  <span>{item.label}</span>
                </AppLink>
              );
            })}
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="min-w-0 flex-1 p-4 sm:p-6 md:p-8">
          <div className="mx-auto max-w-4xl">{children}</div>
        </main>
      </div>
    </SheetsContext.Provider>
  );
};
